/**
 * API Key Settings Panel (desktop / local-only tab).
 *
 * Allows users to store their own API keys in the OS keyring so pipelines
 * use their personal quotas instead of the shared/default credentials.
 *
 * Mobile-first responsive per CLAUDE.md Rule #13.
 * Never renders the stored value - keys can only be entered, tested,
 * saved, or cleared.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  ExternalLink,
  Loader2,
  Save,
  Trash2,
  X,
} from 'lucide-react';

import { useTranslation } from '../../hooks/useTranslation';
import {
  type ApiKeyCategory,
  type ApiKeyStatusItem,
  type KeyValidationResult,
  deleteApiKey,
  getApiKeysStatus,
  saveApiKey,
  validateApiKey,
} from '../../services/apiKeysService';

// ---------------------------------------------------------------------------
// Static metadata (same set the backend exposes via SUPPORTED_USER_API_KEYS)
// ---------------------------------------------------------------------------

interface ApiKeyConfig {
  keyName: string;
  displayName: string;
  category: ApiKeyCategory;
  descriptionKey: string;
  defaultDescription: string;
  helpUrl?: string;
  desktopOnly?: boolean;
}

const API_KEYS: ApiKeyConfig[] = [
  // LLM providers
  {
    keyName: 'OPENROUTER_API_KEY',
    displayName: 'OpenRouter',
    category: 'llm',
    descriptionKey: 'settings.apiKeys.openrouter.desc',
    defaultDescription: 'Access 100+ LLM models through OpenRouter',
    helpUrl: 'https://openrouter.ai/keys',
  },
  {
    keyName: 'OPENAI_API_KEY',
    displayName: 'OpenAI',
    category: 'llm',
    descriptionKey: 'settings.apiKeys.openai.desc',
    defaultDescription: 'GPT-4, GPT-4o and GPT-4o-mini',
    helpUrl: 'https://platform.openai.com/api-keys',
  },
  {
    keyName: 'ANTHROPIC_API_KEY',
    displayName: 'Anthropic',
    category: 'llm',
    descriptionKey: 'settings.apiKeys.anthropic.desc',
    defaultDescription: 'Claude 3 / 3.5 / 4 models',
    helpUrl: 'https://console.anthropic.com/settings/keys',
  },
  {
    keyName: 'GOOGLE_AI_API_KEY',
    displayName: 'Google AI',
    category: 'llm',
    descriptionKey: 'settings.apiKeys.googleAi.desc',
    defaultDescription: 'Gemini models with free tier',
    helpUrl: 'https://aistudio.google.com/app/apikey',
  },
  {
    keyName: 'GROQ_API_KEY',
    displayName: 'Groq',
    category: 'llm',
    descriptionKey: 'settings.apiKeys.groq.desc',
    defaultDescription: 'Ultra-fast inference with free tier',
    helpUrl: 'https://console.groq.com/keys',
  },
  {
    keyName: 'MISTRAL_API_KEY',
    displayName: 'Mistral',
    category: 'llm',
    descriptionKey: 'settings.apiKeys.mistral.desc',
    defaultDescription: 'Mistral and Mixtral models',
    helpUrl: 'https://console.mistral.ai/api-keys/',
  },
  {
    keyName: 'DEEPSEEK_API_KEY',
    displayName: 'DeepSeek',
    category: 'llm',
    descriptionKey: 'settings.apiKeys.deepseek.desc',
    defaultDescription: 'DeepSeek models with affordable pricing',
    helpUrl: 'https://platform.deepseek.com/api_keys',
  },
  {
    keyName: 'OLLAMA_BASE_URL',
    displayName: 'Ollama (local)',
    category: 'llm',
    descriptionKey: 'settings.apiKeys.ollama.desc',
    defaultDescription: 'Local LLM server URL (e.g. http://localhost:11434)',
  },

  // Media providers
  {
    keyName: 'PEXELS_API_KEY',
    displayName: 'Pexels',
    category: 'media',
    descriptionKey: 'settings.apiKeys.pexels.desc',
    defaultDescription: 'Free stock videos and images (200 req/hr)',
    helpUrl: 'https://www.pexels.com/api/',
  },
  {
    keyName: 'PIXABAY_API_KEY',
    displayName: 'Pixabay',
    category: 'media',
    descriptionKey: 'settings.apiKeys.pixabay.desc',
    defaultDescription: 'Free stock media (unlimited)',
    helpUrl: 'https://pixabay.com/api/docs/',
  },
  {
    keyName: 'FREESOUND_API_KEY',
    displayName: 'Freesound',
    category: 'media',
    descriptionKey: 'settings.apiKeys.freesound.desc',
    defaultDescription: 'Sound effects library',
    helpUrl: 'https://freesound.org/apiv2/apply/',
  },
  {
    keyName: 'YOUTUBE_API_KEY',
    displayName: 'YouTube Data API v3',
    category: 'media',
    descriptionKey: 'settings.apiKeys.youtube.desc',
    defaultDescription: 'YouTube video curation (10k units/day free)',
    helpUrl: 'https://console.cloud.google.com/apis/credentials',
    desktopOnly: true,
  },

  // TTS providers
  {
    keyName: 'AZURE_SPEECH_KEY',
    displayName: 'Azure Speech',
    category: 'tts',
    descriptionKey: 'settings.apiKeys.azureSpeech.desc',
    defaultDescription: 'Microsoft voices with free tier',
    helpUrl: 'https://portal.azure.com/',
  },
  {
    keyName: 'GOOGLE_CLOUD_TTS_KEY',
    displayName: 'Google Cloud TTS',
    category: 'tts',
    descriptionKey: 'settings.apiKeys.googleTts.desc',
    defaultDescription: 'Google Cloud text-to-speech',
    helpUrl: 'https://console.cloud.google.com/apis/credentials',
  },

  // Image generation
  {
    keyName: 'CIVITAI_API_KEY',
    displayName: 'CivitAI',
    category: 'image',
    descriptionKey: 'settings.apiKeys.civitai.desc',
    defaultDescription: 'CivitAI model access (Desktop Only)',
    helpUrl: 'https://civitai.com/user/account',
    desktopOnly: true,
  },
  {
    keyName: 'HF_TOKEN',
    displayName: 'Hugging Face',
    category: 'image',
    descriptionKey: 'settings.apiKeys.hfToken.desc',
    defaultDescription: 'Gated model downloads from Hugging Face (Desktop Only)',
    helpUrl: 'https://huggingface.co/settings/tokens',
    desktopOnly: true,
  },
];

const CATEGORY_META: Record<ApiKeyCategory, { labelKey: string; defaultLabel: string }> = {
  llm: { labelKey: 'settings.apiKeys.category.llm', defaultLabel: 'LLM Providers' },
  media: { labelKey: 'settings.apiKeys.category.media', defaultLabel: 'Media Providers' },
  tts: { labelKey: 'settings.apiKeys.category.tts', defaultLabel: 'TTS Providers' },
  image: { labelKey: 'settings.apiKeys.category.image', defaultLabel: 'Image Generation' },
};

const CATEGORY_ORDER: ApiKeyCategory[] = ['llm', 'media', 'tts', 'image'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ApiKeySettings() {
  const { t } = useTranslation();

  const [keyringAvailable, setKeyringAvailable] = useState<boolean>(true);
  const [statuses, setStatuses] = useState<Record<string, ApiKeyStatusItem>>({});
  const [keyValues, setKeyValues] = useState<Record<string, string>>({});
  const [showKey, setShowKey] = useState<Record<string, boolean>>({});
  const [testing, setTesting] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [validation, setValidation] = useState<Record<string, KeyValidationResult>>({});
  const [expanded, setExpanded] = useState<Record<ApiKeyCategory, boolean>>({
    llm: true,
    media: false,
    tts: false,
    image: false,
  });
  const [loadError, setLoadError] = useState<string | null>(null);

  const refreshStatus = useCallback(async () => {
    try {
      const response = await getApiKeysStatus();
      setKeyringAvailable(response.keyring_available);
      const byName: Record<string, ApiKeyStatusItem> = {};
      for (const item of response.items) {
        byName[item.key_name] = item;
      }
      setStatuses(byName);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load status');
    }
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const byCategory = useMemo(() => {
    const grouped: Record<ApiKeyCategory, ApiKeyConfig[]> = {
      llm: [],
      media: [],
      tts: [],
      image: [],
    };
    for (const entry of API_KEYS) {
      grouped[entry.category].push(entry);
    }
    return grouped;
  }, []);

  const toggleCategory = (category: ApiKeyCategory) => {
    setExpanded((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const handleTest = async (keyName: string) => {
    const value = keyValues[keyName];
    if (!value) return;
    setTesting((prev) => ({ ...prev, [keyName]: true }));
    try {
      const result = await validateApiKey(keyName, value);
      setValidation((prev) => ({ ...prev, [keyName]: result }));
    } catch (err) {
      setValidation((prev) => ({
        ...prev,
        [keyName]: {
          valid: false,
          message: err instanceof Error ? err.message : 'Validation failed',
          quota_remaining: null,
          quota_total: null,
        },
      }));
    } finally {
      setTesting((prev) => ({ ...prev, [keyName]: false }));
    }
  };

  const handleSave = async (keyName: string) => {
    const value = keyValues[keyName];
    if (!value) return;
    setSaving((prev) => ({ ...prev, [keyName]: true }));
    try {
      await saveApiKey(keyName, value);
      setKeyValues((prev) => ({ ...prev, [keyName]: '' }));
      await refreshStatus();
    } catch (err) {
      setValidation((prev) => ({
        ...prev,
        [keyName]: {
          valid: false,
          message: err instanceof Error ? err.message : 'Save failed',
          quota_remaining: null,
          quota_total: null,
        },
      }));
    } finally {
      setSaving((prev) => ({ ...prev, [keyName]: false }));
    }
  };

  const handleClear = async (keyName: string) => {
    try {
      await deleteApiKey(keyName);
      setKeyValues((prev) => ({ ...prev, [keyName]: '' }));
      setValidation((prev) => {
        const next = { ...prev };
        delete next[keyName];
        return next;
      });
      await refreshStatus();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <p className="text-sm text-white/70">
          {t('settings.apiKeys.subtitle', {
            defaultValue:
              'Configure your own API keys to use your personal accounts and quotas. Leave empty to use default keys.',
          })}
        </p>
      </div>

      {/* Keyring unavailable warning */}
      {!keyringAvailable && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-900/20 border border-amber-500/30">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-200">
            {t('settings.apiKeys.keyringUnavailable', {
              defaultValue:
                'OS keyring is not available here. You can still view status from environment variables, but saving keys requires a desktop keyring backend.',
            })}
          </div>
        </div>
      )}

      {/* Load error */}
      {loadError && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-900/20 border border-red-500/30">
          <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-red-200">{loadError}</div>
        </div>
      )}

      {/* Category sections */}
      {CATEGORY_ORDER.map((category) => {
        const entries = byCategory[category];
        const meta = CATEGORY_META[category];
        const isOpen = expanded[category];
        return (
          <div
            key={category}
            className="rounded-lg overflow-hidden bg-black/20 border border-white/10"
          >
            <button
              type="button"
              onClick={() => toggleCategory(category)}
              className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-black/30 transition-colors min-h-[44px]"
              aria-expanded={isOpen}
            >
              <span className="text-sm font-medium text-white">
                {t(meta.labelKey, { defaultValue: meta.defaultLabel })}
              </span>
              {isOpen ? (
                <ChevronUp className="w-4 h-4 text-white/60" />
              ) : (
                <ChevronDown className="w-4 h-4 text-white/60" />
              )}
            </button>

            {isOpen && (
              <div className="p-3 sm:p-4 pt-0 space-y-4">
                {entries.map((entry) => {
                  const status = statuses[entry.keyName];
                  const isSaving = saving[entry.keyName];
                  const isTesting = testing[entry.keyName];
                  const val = keyValues[entry.keyName] ?? '';
                  const validationResult = validation[entry.keyName];
                  return (
                    <div key={entry.keyName} className="space-y-1.5">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <label
                          htmlFor={`apikey-${entry.keyName}`}
                          className="text-sm font-medium text-white"
                        >
                          {entry.displayName}
                        </label>
                        {status?.configured && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-400">
                            <Check className="w-3 h-3" />
                            {status.source === 'user'
                              ? t('settings.apiKeys.sourceUser', {
                                  defaultValue: 'User key',
                                })
                              : t('settings.apiKeys.sourceEnv', {
                                  defaultValue: 'Env var',
                                })}
                          </span>
                        )}
                        {entry.desktopOnly && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-900/30 rounded px-1.5 py-0.5">
                            {t('settings.apiKeys.desktopOnly', {
                              defaultValue: 'Desktop only',
                            })}
                          </span>
                        )}
                        {entry.helpUrl && (
                          <a
                            href={entry.helpUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-purple-300 hover:text-purple-200"
                          >
                            {t('settings.apiKeys.getKey', { defaultValue: 'Get key' })}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                      <p className="text-xs text-white/50">
                        {t(entry.descriptionKey, { defaultValue: entry.defaultDescription })}
                      </p>

                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <div className="relative flex-1">
                          <input
                            id={`apikey-${entry.keyName}`}
                            type={showKey[entry.keyName] ? 'text' : 'password'}
                            value={val}
                            onChange={(e) =>
                              setKeyValues((prev) => ({
                                ...prev,
                                [entry.keyName]: e.target.value,
                              }))
                            }
                            placeholder={
                              status?.configured
                                ? t('settings.apiKeys.placeholderConfigured', {
                                    defaultValue: 'Enter new key to replace…',
                                  })
                                : t('settings.apiKeys.placeholder', {
                                    defaultValue: 'Enter API key…',
                                  })
                            }
                            autoComplete="off"
                            spellCheck={false}
                            className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 pr-12 text-white text-sm min-h-[44px] focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 transition-colors"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setShowKey((prev) => ({
                                ...prev,
                                [entry.keyName]: !prev[entry.keyName],
                              }))
                            }
                            className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-white/40 hover:text-white/80"
                            aria-label={t('settings.apiKeys.toggleVisibility', {
                              defaultValue: 'Toggle visibility',
                            })}
                          >
                            {showKey[entry.keyName] ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleTest(entry.keyName)}
                            disabled={!val || isTesting}
                            className="flex items-center gap-1.5 px-3 py-2 min-h-[44px] bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-sm text-white transition-colors"
                          >
                            {isTesting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                            {t('settings.apiKeys.test', { defaultValue: 'Test' })}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSave(entry.keyName)}
                            disabled={!val || isSaving || !keyringAvailable}
                            className="flex items-center gap-1.5 px-3 py-2 min-h-[44px] bg-purple-600 hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-sm text-white transition-colors"
                          >
                            {isSaving ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Save className="w-4 h-4" />
                            )}
                            {t('settings.apiKeys.save', { defaultValue: 'Save' })}
                          </button>
                          {status?.source === 'user' && (
                            <button
                              type="button"
                              onClick={() => handleClear(entry.keyName)}
                              className="flex items-center gap-1.5 px-3 py-2 min-h-[44px] bg-red-900/30 hover:bg-red-900/50 rounded-lg text-sm text-red-200 transition-colors"
                              aria-label={t('settings.apiKeys.clear', { defaultValue: 'Clear' })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {validationResult && (
                        <div
                          className={`flex items-start gap-1.5 text-xs mt-1 ${
                            validationResult.valid ? 'text-emerald-400' : 'text-red-400'
                          }`}
                        >
                          {validationResult.valid ? (
                            <Check className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          ) : (
                            <X className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          )}
                          <span>{validationResult.message}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
